import { useCallback } from 'react';
import { ApiService, JobStatusResponse } from 'api';
import { ERROR_403 } from 'common/config/api';
import { useCheckReportFile, useResponse } from 'services/hooks';
import { Logger } from 'utilities';
import {
  DEFAULT_ERROR_MESSAGE,
  INFO_MESSAGE,
  KEEP_POLLING_STATUS_CODES,
  MAX_ATTEMPTS_MESSAGE,
  ON_SUCCESS_MESSAGE,
  ON_SUCCESS_NOTIFICATION_MESSAGE,
} from '../constants';

const useReportsZipJob = (firmId: number) => {
  const { successNotification, errorNotification } = useResponse();

  // success will have a signed_url to download the zip file
  const onSuccessForZipReport = useCallback(
    (onSuccessData: JobStatusResponse, _requestData: {}) => {
      globalThis.location.href = onSuccessData.signed_url as string;
      successNotification(ON_SUCCESS_NOTIFICATION_MESSAGE);
    },
    [successNotification]
  );

  const pollingFn = useCallback(
    (reportUUID: string, _requestData: {}) =>
      ApiService.apiExcelExportsFullCompanyReportsDownloadJobRead(firmId.toString(), reportUUID),
    [firmId]
  );

  // need setReportRequestData to set something truthy so the polling takes place
  const {
    setDoReportPollCheck,
    setReportRequestData,
    setReportUUID,
    isLoading: isPollingZipJob,
  } = useCheckReportFile<{}, JobStatusResponse, void>({
    successMessage: ON_SUCCESS_MESSAGE,
    defaultErrorMessage: DEFAULT_ERROR_MESSAGE,
    maxAttemptsMessage: MAX_ATTEMPTS_MESSAGE,
    infoMessage: INFO_MESSAGE,
    keepPollingStatusCodes: KEEP_POLLING_STATUS_CODES,
    onSuccessFn: onSuccessForZipReport,
    pollingFn,
  });

  const startZipJob = useCallback(
    async (company_measurement_dates_ids: number[]) => {
      const requestData = {
        firm: firmId,
        company_measurement_dates: company_measurement_dates_ids,
      };
      try {
        const { job_id } = await ApiService.apiExcelExportsFullCompanyReportsDownloadJobCreate(
          firmId.toString(),
          requestData
        );
        setReportUUID(job_id!);
        setDoReportPollCheck(true);
        setReportRequestData({});
      } catch (error) {
        if ((error as any)?.status === Number(ERROR_403)) {
          errorNotification(
            'You do not have permission to start a reports zip job for this firm. Contact support if you believe this is an error.'
          );
        } else {
          errorNotification('Error starting reports zip job');
        }
        Logger.error('Error starting reports zip job', error);
        setDoReportPollCheck(false);
      }
    },
    [firmId, errorNotification, setDoReportPollCheck, setReportUUID, setReportRequestData]
  );

  return {
    startZipJob,
    isPollingZipJob,
  };
};

export default useReportsZipJob;
